import React, { useEffect, useState } from "react";
import { Button, Form, Input, Space, Spin, Table, Select } from "antd";
import {
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import NewInviteModal from "./Modals/NewInviteModal";
import RemoveInviteModal from "./Modals/RemoveInviteModal";

import { createNotification } from "../../../components";
import {
  deleteInvite,
  deleteMember,
  getRoles,
  partnerInvites,
  updateMemberRole,
} from "../../../services/account/api";
import { FormattedMessage, useIntl } from "react-intl";
import { useAuth } from "../../../contexts/auth";
import RemoveUserModal from "./Modals/RemoveUserModal";

const { Search } = Input;

export default function Team() {
  const intl = useIntl();

  const { user, HasPermission } = useAuth();

  const [newInviteModal, setNewInviteModal] = useState(false);
  const [removeMemberModal, setRemoveMemberModal] = useState(false);
  const [removeInviteModal, setRemoveInviteModal] = useState(false);
  const [options, setOptions] = useState([]);
  const [removeEmail, setRemoveEmail] = useState("");
  const [invites, setInvites] = useState([]);
  const [isLoading, setLoading] = useState(true);

  const [search, setSearch] = useState("");

  useEffect(() => {
    loadInvites();
    getRoles()
      .then(setOptions)
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const loadInvites = async () => {
    await partnerInvites().then(setInvites);
  };

  const removeMember = async () => {
    await deleteMember(removeEmail)
      .then(() => {
        createNotification("success", "Member removed.");
        setRemoveMemberModal(false);
        loadInvites();
      })
      .catch(() => {
          createNotification(
              "error",
              intl.formatMessage({
                  id: "page.invite.delete_member_failed.notification",
              }),
          );
      });
  };

  const removeInvite = async () => {
    await deleteInvite(removeEmail)
      .then(() => {
        createNotification("success", "Invite removed.");
        setRemoveInviteModal(false);
        loadInvites();
      })
      .catch(() => {
          createNotification(
              "error",
              intl.formatMessage({
                  id: "page.invite.delete_invite_failed.notification",
              }),
          );
      });
  };

  const handleChangeRole = async (email, value) => {
    await updateMemberRole(email, { role: value }).then(() => {
      createNotification('success', 'Perfil alterado com sucesso');
    }).catch((err) => {
      console.error(err);
      createNotification('error', 'Problema ao alterar perfil');
    });
  };

  function renderAction(item) {
      if (HasPermission('delete_invite')) {
          if (item.type == 'invite') {
              return (
                  <Space size="middle">
                    <Link
                      onClick={() => {
                        setRemoveEmail(item.email);
                        setRemoveInviteModal(true);
                      }}
                    >
                      <DeleteOutlined size={18} />
                    </Link>
                  </Space>
              )
          }
      }
      if (HasPermission('delete_member')) {
          if (item.type == 'member' && user.email !== item.email) {
              return (
                  <Space size="middle">
                    <Link
                      onClick={() => {
                        setRemoveEmail(item.email);
                        setRemoveMemberModal(true);
                      }}
                    >
                      <DeleteOutlined size={18} />
                    </Link>
                  </Space>
              )
          }
      }
  }

  const columns = [
    {
      title: "E-mail",
      width: 250,
      key: "email",
      dataIndex: "email",
      render: (_, record) => {
          return (
              <div className="flex gap-2">
              <span>{record.email}</span>
              {user.email == record.email && (
                  <span className="text-gray-400">({intl.formatMessage({ id: "page.invite.list.you"})})</span>
              )}
              </div>
          )
      }
    },
    {
      title: intl.formatMessage({ id: "role" }),
      width: 130,
      key: "role",
      dataIndex: "role",
      render: (data, record) => {
        if (HasPermission('edit_member')) {
          if (record.type === "member") {
            return (
              <Select
                style={{ width: '70%' }}
                disabled={user.email == record.email}
                defaultValue={data}
                onChange={(value) => handleChangeRole(record.email, value)}
                options={options.map((item) => ({
                  label: intl.formatMessage({ id: item.name }),
                  value: item.name,
                }))}
              />
            );
          }
        }
        return <FormattedMessage id={data} />;
      },
    },
    {
      title: "Status",
      width: 100,
      key: "status",
      dataIndex: "status",
      render: (_, record) => {
          if (record.type === 'invite' && record.status === "P") {
              return (
                  <span className="text-gray-500">
                  {intl.formatMessage({ id: "invite_sent" })}
                  </span>
              );
          }
          return (
              <span className="text-primary">
                {intl.formatMessage({ id: "active" })}
              </span>
          );
      },
    },
    {
      title: intl.formatMessage({ id: "action" }),
      width: 80,
      key: "action",
      render: (_, record) => {
          return renderAction(record)
      }
    },
  ];

  if (isLoading) {
    const antIcon = <LoadingOutlined style={{ fontSize: 50 }} spin />;

    return (
      <div className="flex justify-center items-center">
        <Spin size="large" indicator={antIcon} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <Form className="flex justify-between">
          <Space>
            <Search
              placeholder={intl.formatMessage({
                id: "search",
              })}
              allowClear
              size="large"
              onSearch={setSearch}
              onClick={() => setSearch}
            />
          </Space>
          {HasPermission("send_invite") && (
            <Space>
              <Button
                className="bg-primary border-0 text-white !px-6 shadow-none flex items-center"
                size="large"
                shape="round"
                type="primary"
                onClick={() => setNewInviteModal(true)}
              >
                <PlusOutlined />{" "}
                <FormattedMessage id="page.invite.modal.title" />
              </Button>
            </Space>
          )}
        </Form>
      </div>
      <Table
        scroll={{ y: 500 }}
        pagination={false}
        columns={columns}
        dataSource={invites.filter((item) =>
          item.email.toLowerCase().includes(search.toLowerCase()),
        )}
      />
      <NewInviteModal
        open={newInviteModal}
        onCancel={() => setNewInviteModal(false)}
        onSubmit={() => {
          setNewInviteModal(false);
          loadInvites();
        }}
      />
      <RemoveUserModal
        open={removeMemberModal}
        onCancel={() => setRemoveMemberModal(false)}
        onSubmit={() => removeMember()}
      />
      <RemoveInviteModal
        open={removeInviteModal}
        onCancel={() => setRemoveInviteModal(false)}
        onSubmit={() => removeInvite()}
      />
    </div>
  );
}
